import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

const DURATION = 350;
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');
    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');
    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');
    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');
    return summary;
  }

  /** The element that wraps everything except the summary */
  get content() {
    const content = this.details.querySelector(':scope > :not(summary)');
    if (!(content instanceof HTMLElement)) throw new Error('Content element not found');
    return content;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #controller = new AbortController();
  /** @type {Animation | null} */
  #animation = null;

  connectedCallback() {
    const { signal } = this.#controller;

    // Take over exclusive-group behaviour from the browser so the closing
    // sibling can animate instead of being snapped shut by the `name` attribute.
    if (this.details.hasAttribute('name')) {
      this.dataset.accordionGroup = this.details.getAttribute('name') ?? '';
      this.details.removeAttribute('name');
    }

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#animation?.cancel();
  }

  /**
   * @param {Event} event
   */
  handleClick = (event) => {
    event.preventDefault();

    const isMobile = isMobileBreakpoint();
    if ((isMobile && this.#disableOnMobile) || (!isMobile && this.#disableOnDesktop)) return;

    this.toggle();
  };

  toggle() {
    this.details.open ? this.close() : this.open();
  }

  open() {
    this.#closeSiblings();

    this.#animation?.cancel();
    const { details, content } = this;

    details.open = true;

    if (this.#reducedMotion) return;

    // Padding sits outside `height`, so it must be animated too
    // (e.g. the accordion--custom-background variant).
    const { paddingTop, paddingBottom } = getComputedStyle(content);
    const height = content.scrollHeight - parseFloat(paddingTop) - parseFloat(paddingBottom);
    content.style.overflow = 'hidden';

    this.#animation = content.animate(
      [
        { height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 },
        { height: `${height}px`, paddingTop, paddingBottom, opacity: 1 },
      ],
      { duration: DURATION, easing: EASING }
    );

    this.#animation.onfinish = () => this.#cleanup();
  }

  close() {
    this.#animation?.cancel();
    const { details, content } = this;

    if (!details.open) return;

    if (this.#reducedMotion) {
      details.open = false;
      return;
    }

    const { paddingTop, paddingBottom } = getComputedStyle(content);
    const height = content.getBoundingClientRect().height - parseFloat(paddingTop) - parseFloat(paddingBottom);
    content.style.overflow = 'hidden';

    this.#animation = content.animate(
      [
        { height: `${height}px`, paddingTop, paddingBottom, opacity: 1 },
        { height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0 },
      ],
      { duration: DURATION, easing: EASING }
    );

    this.#animation.onfinish = () => {
      details.open = false;
      this.#cleanup();
    };
  }

  #cleanup() {
    this.content.style.overflow = '';
    this.#animation = null;
  }

  /** Close any other open accordion in the same group at the same time */
  #closeSiblings() {
    const group = this.dataset.accordionGroup;
    if (!group) return;

    document
      .querySelectorAll(`accordion-custom[data-accordion-group="${group}"]`)
      .forEach((el) => {
        if (el !== this && el instanceof AccordionCustom) el.close();
      });
  }

  /**
   * @param {MouseEvent} event
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;
    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    this.close();
  };

  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.#animation?.cancel();
    this.#cleanup();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();
      this.summary.focus();
      this.close();
    }
  };
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}